import { useMemo } from "react";

import dayjs from "dayjs";

import type { ActivationAlertsContractNewsData, ActivationAlertsNewsDetailData } from "@similarweb/pro-api-client";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import { useApiQuery } from "app/modules/api-client";

import type { ValueMap } from "../../filters/types";
import { useCommonAlertsQueryParams } from "../../useCommonAlertsQueryParams";

import { useNewsAlertsFilterStore } from "../filters/useNewsAlertsFilterStore";
import type { NewsType } from "../types";

export type NewsAlertItem = ActivationAlertsContractNewsData & {
    formattedDate: string;
};

const isMatchingNewsType = (
    alert: ActivationAlertsContractNewsData,
    selectedNewsTypes: ValueMap<NewsType>,
): boolean => {
    return selectedNewsTypes.has(alert.newsType);
};

const matchesSearch = (alert: ActivationAlertsContractNewsData, searchValue: string): boolean => {
    if (!searchValue) {
        return true;
    }

    const lowerSearch = searchValue.toLowerCase();

    return (
        alert.domain?.toLowerCase().includes(lowerSearch) ||
        alert.publisherDomain?.toLowerCase().includes(lowerSearch) ||
        alert.headline?.toLowerCase().includes(lowerSearch) ||
        alert.content?.toLowerCase().includes(lowerSearch) ||
        false
    );
};

const select = (data: ActivationAlertsNewsDetailData): NewsAlertItem[] =>
    data.map((item) => ({
        ...item,
        formattedDate: dayjs(item.publishedDate).format(shortMonthDayAndYear),
    }));

export const useNewsAlerts = () => {
    const searchValue = useNewsAlertsFilterStore((s) => s.searchValue);
    const selectedTopics = useNewsAlertsFilterStore((s) => s.selectedTopics);
    const selectedNewsTypes = useNewsAlertsFilterStore((s) => s.selectedNewsTypes);
    const isDefaultTopicsSelected = useNewsAlertsFilterStore((s) => s.isDefaultTopicsSelected);
    const isDefaultNewsTypesSelected = useNewsAlertsFilterStore((s) => s.isDefaultNewsTypesSelected);
    const params = useCommonAlertsQueryParams();

    const { data, ...rest } = useApiQuery(
        {
            tag: "Alerts",
            methodName: "activationAlertsNewsDetail",
            options: {
                select,
                refetchOnMount: true,
            },
        },
        params,
    );

    const filteredData = useMemo(
        () =>
            data?.filter((alert) => {
                const isValidTopic = isDefaultTopicsSelected || selectedTopics.has(alert.topic);
                const isValidNewsType = isDefaultNewsTypesSelected || isMatchingNewsType(alert, selectedNewsTypes);

                return isValidTopic && isValidNewsType && matchesSearch(alert, searchValue);
            }),
        [data, searchValue, selectedTopics, selectedNewsTypes, isDefaultTopicsSelected, isDefaultNewsTypesSelected],
    );

    return { data: filteredData, ...rest };
};
